package com.arenaproplay.admin.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.data.auth.AuthError
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.navigation.Routes
import com.arenaproplay.admin.state.auth.LocalAuthSession
import com.arenaproplay.admin.ui.localization.LocalAppStrings
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.AppEffects
import com.arenaproplay.admin.ui.theme.toHexString
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.displayMedium
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import com.arenaproplay.admin.utils.PageSeo
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input

@Page("/login")
@Composable
fun LoginPage() {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val session = LocalAuthSession.current
    val strings = LocalAppStrings.current
    val scope = rememberCoroutineScope()

    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    PageSeo(title = "Arena Admin · Вход")

    LaunchedEffect(session.isAuthenticated, session.isAdmin) {
        if (session.isAuthenticated && session.isAdmin) {
            ctx.router.navigateTo(Routes.DASHBOARD)
        }
    }

    // Full-page background
    Box(
        Modifier
            .fillMaxSize()
            .backgroundColor(AppColors.Background)
            .styleModifier { property("overflow", "hidden") },
        contentAlignment = Alignment.Center
    ) {
        // Subtle radial ambient glows
        Box(
            Modifier.fillMaxSize().styleModifier {
                property(
                    "background",
                    "radial-gradient(900px 700px at 20% 15%, rgba(37,69,255,0.07), transparent 60%), " +
                        "radial-gradient(700px 550px at 80% 85%, rgba(11,171,100,0.06), transparent 60%)"
                )
                property("pointer-events", "none")
                property("position", "absolute")
                property("inset", "0")
            }
        )

        // Centered login card
        LoginCard(
            email = email,
            password = password,
            loading = loading,
            error = error,
            onEmail = { email = it },
            onPassword = { password = it },
            strings = strings,
            onSubmit = {
                if (loading) return@LoginCard
                if (email.isBlank() || password.isBlank()) {
                    error = strings.loginErrorInvalid
                    return@LoginCard
                }
                error = null
                loading = true
                scope.launch {
                    val result = container.authRepository.login(email.trim(), password)
                    loading = false
                    result.onSuccess { resp ->
                        val tokens = resp.resolveTokens()
                        val user = resp.resolveUser()
                        if (tokens == null || user == null) {
                            error = strings.errorGeneric
                            return@onSuccess
                        }
                        if (!user.isAdmin) {
                            error = strings.loginErrorNotAdmin
                            return@onSuccess
                        }
                        session.signIn(tokens, user)
                        container.createAuthenticatedClients(
                            accessToken = tokens.accessToken,
                            refreshToken = tokens.refreshToken,
                            onTokensRefreshed = { newAccess, newRefresh ->
                                session.updateTokens(newAccess, newRefresh)
                            },
                            onAuthFailed = {
                                session.signOut()
                                ctx.router.navigateTo(Routes.LOGIN)
                            },
                        )
                        ctx.router.navigateTo(Routes.DASHBOARD)
                    }.onFailure {
                        error = when (it) {
                            is AuthError.InvalidCredentials -> strings.loginErrorInvalid
                            is AuthError.Network -> "${strings.errorGeneric}: ${it.message}"
                            else -> strings.errorGeneric
                        }
                    }
                }
            }
        )
    }
}

@Composable
private fun LoginCard(
    email: String,
    password: String,
    loading: Boolean,
    error: String?,
    onEmail: (String) -> Unit,
    onPassword: (String) -> Unit,
    strings: com.arenaproplay.admin.ui.localization.AppStrings,
    onSubmit: () -> Unit
) {
    Column(
        Modifier
            .styleModifier { property("width", "min(400px, calc(100vw - 32px))") }
            .sBorderRadius(16)
            .sPadding(36)
            .styleModifier {
                property("background", "#${AppColors.Surface.toHexString()}")
                property("border", "1px solid #${AppColors.Border.toHexString()}")
                property("box-shadow", AppEffects.CardElevation)
                // arena-rise entrance animation
                property("animation", "arena-rise 280ms cubic-bezier(0.16, 1, 0.3, 1) both")
            },
        horizontalAlignment = Alignment.Start
    ) {
        // Logo block — matches rail LogoChip style
        LogoBlock()

        Box(Modifier.height(28.px))

        // Card heading
        SpanText(
            "Вход в админ-панель",
            Modifier.displayMedium().color(AppColors.Ink)
        )
        Box(Modifier.height(6.px))
        SpanText(
            strings.loginSubtitle,
            Modifier.bodyMRegular().color(AppColors.Muted)
        )

        Box(Modifier.height(28.px))

        // Email field
        FieldLabel("Почта")
        LoginInput(
            value = email,
            placeholderText = "you@arena.io",
            type = InputType.Email,
            onChange = onEmail
        )
        Box(Modifier.height(16.px))

        // Password field
        FieldLabel("Пароль")
        LoginInput(
            value = password,
            placeholderText = "••••••••",
            type = InputType.Password,
            onChange = onPassword
        )

        // Error banner
        if (error != null) {
            Box(Modifier.height(16.px))
            Row(
                Modifier
                    .fillMaxWidth()
                    .sBorderRadius(10)
                    .sPadding(10, 14)
                    .styleModifier {
                        property("background", "#${AppColors.Accent.DangerTint.toHexString()}")
                        property("border", "1px solid rgba(208,92,70,0.25)")
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    "!",
                    Modifier.styleModifier {
                        property("font-size", "13px")
                        property("font-weight", "700")
                        property("color", "#${AppColors.Error.toHexString()}")
                        property("line-height", "1")
                    }
                )
                Box(Modifier.width(10.px))
                SpanText(error, Modifier.bodySMedium().color(AppColors.Error))
            }
        }

        Box(Modifier.height(24.px))

        // Primary submit button
        Box(
            Modifier
                .fillMaxWidth()
                .sBorderRadius(12)
                .styleModifier {
                    property("background", if (loading) "#${AppColors.PrimaryHover.toHexString()}" else "#${AppColors.Primary.toHexString()}")
                    property("transition", "background 160ms ease, transform 160ms ease, box-shadow 160ms ease")
                    property("box-shadow", "0 2px 8px rgba(37,69,255,0.28)")
                    property("cursor", if (loading) "wait" else "pointer")
                }
                .onClick { onSubmit() }
        ) {
            Row(
                Modifier.fillMaxWidth().sPadding(14, 18),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    if (loading) strings.loading else "Войти",
                    Modifier.styleModifier {
                        property("font-family", "'Unbounded', sans-serif")
                        property("font-size", "14px")
                        property("font-weight", "600")
                        property("color", "#${AppColors.Surface.toHexString()}")
                        property("letter-spacing", "0.01em")
                    }
                )
                Box(Modifier.weight(1f))
                if (!loading) {
                    SpanText(
                        "→",
                        Modifier.styleModifier {
                            property("font-size", "16px")
                            property("color", "rgba(255,255,255,0.75)")
                        }
                    )
                }
            }
        }

        Box(Modifier.height(16.px))

        // Footer note
        SpanText(
            "Требуется роль администратора · учётные записи управляются в Arena Backend",
            Modifier.styleModifier {
                property("font-size", "12px")
                property("color", "#${AppColors.Faint.toHexString()}")
                property("line-height", "1.5")
            }
        )
    }
}

/** Logo block matching the rail LogoChip style. */
@Composable
private fun LogoBlock() {
    Row(verticalAlignment = Alignment.CenterVertically) {
        // Gradient «A» square
        Box(
            Modifier
                .width(28.px)
                .height(28.px)
                .sBorderRadius(7)
                .styleModifier {
                    property("background", "linear-gradient(135deg,#${AppColors.Primary.toHexString()},#${AppColors.PrimaryHover.toHexString()})")
                    property("flex-shrink", "0")
                    property("box-shadow", "0 2px 6px rgba(37,69,255,0.35)")
                },
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                "A",
                Modifier.styleModifier {
                    property("font-family", "'Unbounded', sans-serif")
                    property("font-weight", "700")
                    property("font-size", "13px")
                    property("color", "#${AppColors.Surface.toHexString()}")
                    property("line-height", "1")
                }
            )
        }

        Box(Modifier.width(9.px))

        // Brand text
        Column {
            SpanText(
                "Arena Pro Play",
                Modifier.styleModifier {
                    property("font-family", "'Unbounded', sans-serif")
                    property("font-weight", "700")
                    property("font-size", "13px")
                    property("color", "#${AppColors.Ink.toHexString()}")
                    property("line-height", "1")
                    property("white-space", "nowrap")
                }
            )
            Box(Modifier.height(3.px))
            SpanText(
                "Admin Console",
                Modifier.styleModifier {
                    property("font-size", "9.5px")
                    property("color", "#${AppColors.Faint.toHexString()}")
                    property("white-space", "nowrap")
                }
            )
        }
    }
}

@Composable
private fun FieldLabel(label: String) {
    SpanText(
        label,
        Modifier.bodySMedium().color(AppColors.Muted)
    )
    Box(Modifier.height(7.px))
}

@Composable
private fun LoginInput(
    value: String,
    placeholderText: String,
    type: InputType<String>,
    onChange: (String) -> Unit
) {
    Box(
        Modifier
            .fillMaxWidth()
            .sBorderRadius(10)
            .styleModifier {
                property("background", "#${AppColors.Background.toHexString()}")
                property("border", "1px solid #${AppColors.Border.toHexString()}")
                property("transition", "border-color 200ms ease, box-shadow 200ms ease")
                property("padding", "12px 14px")
            }
    ) {
        Input(
            type = type,
            attrs = Modifier.fillMaxWidth().toAttrs {
                value(value)
                placeholder(placeholderText)
                onInput { onChange(it.value) }
                style {
                    property("background", "transparent")
                    property("border", "none")
                    property("outline", "none")
                    property("color", "#${AppColors.Ink.toHexString()}")
                    property("font-family", "Inter, sans-serif")
                    property("font-size", "14px")
                    property("font-weight", "450")
                    property("width", "100%")
                    property("letter-spacing", "0.01em")
                }
                onFocus {
                    it.target.asDynamic().parentElement?.style?.setProperty("border-color", "#${AppColors.Primary.toHexString()}")
                    it.target.asDynamic().parentElement?.style?.setProperty("box-shadow", AppEffects.InputFocusRing)
                }
                onBlur {
                    it.target.asDynamic().parentElement?.style?.setProperty("border-color", "#${AppColors.Border.toHexString()}")
                    it.target.asDynamic().parentElement?.style?.setProperty("box-shadow", "none")
                }
            }
        )
    }
}
